/**
 * PhaseFormModal (#179) — modal "Editar fase" com nome + cor (swatches).
 * Substitui a edição inline do nome no header do acordeão (que não tinha cor).
 */

import React, { useEffect, useState } from 'react';

import { Button, Input, Modal, ModalContent, ModalFooter, ModalHeader } from '@/shared/components/ui';

import { PHASE_COLORS } from '../../hooks/useProjectDraft';

export interface PhaseFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialName: string;
  initialColor: string;
  onSave: (patch: { name: string; color: string }) => void;
}

export const PhaseFormModal: React.FC<PhaseFormModalProps> = ({
  isOpen,
  onClose,
  initialName,
  initialColor,
  onSave,
}) => {
  const [name, setName] = useState(initialName);
  const [color, setColor] = useState(initialColor);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setColor(initialColor);
    }
  }, [isOpen, initialName, initialColor]);

  const handleSave = (): void => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave({ name: trimmed, color });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md">
      <ModalHeader onClose={onClose}>Editar fase</ModalHeader>
      <ModalContent>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
              Nome da fase
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome da fase"
              data-testid="phase-modal-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
              Cor
            </label>
            <div className="flex flex-wrap gap-2.5" data-testid="phase-modal-colors">
              {PHASE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className="w-8 h-8 rounded-full transition-transform hover:scale-110"
                  style={{
                    backgroundColor: c,
                    outline: color === c ? '2px solid var(--theme-primary)' : '2px solid transparent',
                    outlineOffset: 2,
                  }}
                  aria-label={`Selecionar cor ${c}`}
                  data-testid={`phase-modal-color-${c}`}
                />
              ))}
            </div>
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} data-testid="phase-modal-cancel">
          Cancelar
        </Button>
        <Button icon="check" disabled={!name.trim()} onClick={handleSave} data-testid="phase-modal-save">
          Salvar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

PhaseFormModal.displayName = 'PhaseFormModal';
